/**
 * @file ConflictResolutionModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-04-01
 * @modified 2026-04-01
 * @description Modal for resolving field-level conflicts between local data and Xero
 */

import React, { useState, useMemo } from 'react';
import Modal from './Modal';
import { formatDateTime } from '../utils/dateFormatters';
import './ConflictResolutionModal.css';

const ENTITY_LABELS = {
  item: 'inventory item',
  contact: 'contact',
  bill: 'bill'
};

function ConflictResolutionModal({
  isOpen,
  onClose,
  onResolve,
  entityType = 'item',
  conflicts = [],
  userChangedFields = new Set(),
  xeroUpdatedAt
}) {
  // Initialise resolutions with smart defaults:
  // - fields the user changed → default to 'local' (keep mine)
  // - fields the user didn't change → default to 'xero' (accept Xero's)
  const initialResolutions = useMemo(() => {
    const resolutions = {};
    for (const c of conflicts) {
      resolutions[c.field] = userChangedFields.has(c.field) ? 'local' : 'xero';
    }
    return resolutions;
  }, [conflicts, userChangedFields]);

  const [resolutions, setResolutions] = useState(initialResolutions);

  // Reset resolutions when conflicts change
  React.useEffect(() => {
    setResolutions(initialResolutions);
  }, [initialResolutions]);

  const handleToggle = (field, value) => {
    setResolutions(prev => ({ ...prev, [field]: value }));
  };

  const handleResolve = () => {
    const xeroValues = {};
    for (const c of conflicts) {
      if (resolutions[c.field] === 'xero') {
        xeroValues[c.field] = c.xeroValue;
      }
    }
    onResolve({ resolutions, xeroValues });
  };

  const trueConflictCount = conflicts.filter(c => userChangedFields.has(c.field)).length;
  const entityLabel = ENTITY_LABELS[entityType] || entityType;

  const footer = (
    <>
      <button className="btn-secondary" onClick={onClose}>Cancel</button>
      <button className="btn-primary" onClick={handleResolve}>Save with Resolutions</button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Conflict Detected"
      size="lg"
      className="conflict-resolution-modal"
      footer={footer}
    >
      <div className="conflict-info">
        <div className="conflict-warning">
          <span className="material-symbols-rounded" style={{ color: 'var(--color-warning)', fontSize: '20px' }}>warning</span>
          <span>
            This {entityLabel} was modified in Xero
            {xeroUpdatedAt && <> on <strong>{formatDateTime(xeroUpdatedAt)}</strong></>}
            {' '}since your last sync.
          </span>
        </div>
        {trueConflictCount > 0 && (
          <p className="conflict-note">
            {trueConflictCount} field{trueConflictCount !== 1 ? 's' : ''} you changed also changed in Xero (highlighted below).
          </p>
        )}
      </div>

      <div className="conflict-table-wrapper">
        <table className="conflict-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>Your Value</th>
              <th>Xero Value</th>
              <th className="keep-col">Keep</th>
            </tr>
          </thead>
          <tbody>
            {conflicts.map(c => {
              const isTrueConflict = userChangedFields.has(c.field);
              return (
                <tr key={c.field} className={isTrueConflict ? 'true-conflict' : ''}>
                  <td className="field-label">{c.fieldLabel}</td>
                  <td className={`value-cell ${resolutions[c.field] === 'local' ? 'selected' : ''}`}>
                    {c.localValue || <span className="empty-value">(empty)</span>}
                  </td>
                  <td className={`value-cell ${resolutions[c.field] === 'xero' ? 'selected' : ''}`}>
                    {c.xeroValue || <span className="empty-value">(empty)</span>}
                  </td>
                  <td className="keep-col">
                    <div className="radio-group">
                      <label className={`radio-label ${resolutions[c.field] === 'local' ? 'active' : ''}`}>
                        <input
                          type="radio"
                          name={`resolve-${c.field}`}
                          checked={resolutions[c.field] === 'local'}
                          onChange={() => handleToggle(c.field, 'local')}
                        />
                        Mine
                      </label>
                      <label className={`radio-label ${resolutions[c.field] === 'xero' ? 'active' : ''}`}>
                        <input
                          type="radio"
                          name={`resolve-${c.field}`}
                          checked={resolutions[c.field] === 'xero'}
                          onChange={() => handleToggle(c.field, 'xero')}
                        />
                        Xero's
                      </label>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

export default ConflictResolutionModal;
